import { useEffect, useState } from 'react';
import { Check, Loader2, Mic2 } from 'lucide-react';
import { apiErrorMessage } from '../api/errors';
import { voiceClonerApi } from '../api/voiceClonerApi';
import { StudioHeader } from '../components/studio';
import { Btn } from '../components/ui';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';

export default function VoiceCloner() {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showToast = useUiStore((s) => s.showToast);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const status = await voiceClonerApi.getNotificationStatus();
        if (cancelled) return;
        setSubscribed(status.subscribed);
      } catch (err) {
        if (cancelled) return;
        const message = apiErrorMessage(err);
        setError(message);
        showToast(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  async function notifyMe() {
    if (subscribed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const status = await voiceClonerApi.subscribe();
      setSubscribed(status.subscribed);
      showToast("You're on the Voice Cloner notify list");
    } catch (err) {
      const message = apiErrorMessage(err);
      setError(message);
      showToast(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <StudioHeader title="Voice Cloner" subtitle="Your voice your music" />

      <section
        aria-label="Voice Cloner notify list"
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: c.surface2,
            boxShadow: `0 0 0 1px ${c.border} inset`,
          }}
        >
          <Mic2 size={18} color={c.text2} />
        </div>

        <p style={{ fontSize: 13, color: c.text1, margin: 0, lineHeight: 1.65, maxWidth: 620 }}>
          We&apos;re building something special. Soon you&apos;ll be able to create songs with a personalized AI
          version of your voice
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
          <Btn
            onClick={() => void notifyMe()}
            disabled={loading || submitting || subscribed}
            aria-label={
              subscribed
                ? "You're on the list"
                : loading
                  ? 'Loading notification status'
                  : submitting
                    ? 'Joining the notify list'
                    : 'Notify me'
            }
            style={{ cursor: loading || submitting || subscribed ? 'default' : 'pointer' }}
          >
            {subscribed ? (
              <Check size={13} />
            ) : loading || submitting ? (
              <Loader2 size={13} className="lyx-spin" />
            ) : (
              <Mic2 size={13} />
            )}
            {subscribed ? "You're on the list" : loading ? 'Loading…' : submitting ? 'Saving…' : 'Notify me'}
          </Btn>

          {subscribed ? (
            <p role="status" aria-live="polite" style={{ fontSize: 12, color: c.text2, margin: 0 }}>
              We&apos;ll email your account address when Voice Cloner is ready.
            </p>
          ) : error ? (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: 0 }}>
              {error}
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
